import type { EnvironmentId } from "@cmmd-forge/contracts";

import type { SavedEnvironmentRuntimeState } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import type { RuntimeAuthorityRenewalState } from "../../environments/runtime/cloudRuntimeAuthorityRenewal";
import {
  isDesktopDiagnosticsTab,
  type DesktopDiagnosticsTarget,
} from "./DiagnosticsDesktopTabs.logic";
import type { DiagnosticsTab } from "./DiagnosticsSettings.types";

/**
 * Which runtime the CMMD-attested read should be taken against.
 *
 * Cloud and desktop targets both resolve to a runtime id but by different
 * routes — a sprite through the Cloud panel's selection, a device through the
 * tab itself — and the server and events tabs resolve to neither.
 */
export function selectedDiagnosticsRuntimeId(input: {
  readonly activeTab: DiagnosticsTab;
  readonly desktopTargets: ReadonlyArray<DesktopDiagnosticsTarget>;
  readonly selectedCloudRuntimeId: string | null;
}): string | null {
  if (input.activeTab === "cloud") return input.selectedCloudRuntimeId;
  if (!isDesktopDiagnosticsTab(input.activeTab)) return null;
  return input.desktopTargets.find((target) => target.tabId === input.activeTab)?.runtimeId ?? null;
}

/**
 * Recheck re-reads the target in view, so it is only live where there is
 * something to re-read: the server always, a sprite once one is selected.
 * Events is a view rather than a machine, and a desktop device is registry
 * presence only — neither has a source to poll.
 */
export function canRecheckDiagnosticsTarget(input: {
  readonly activeTab: DiagnosticsTab;
  readonly hasSelectedCloudRuntime: boolean;
}): boolean {
  if (input.activeTab === "server") return true;
  return input.activeTab === "cloud" && input.hasSelectedCloudRuntime;
}

/**
 * The tab to move to, or `null` to stay put.
 *
 * The page opens on the server target because it is the one machine always
 * readable — but on a shared cloud shell it is not offered to everyone, and
 * opening there would show a panel whose every request comes back 403 under a
 * name the picker does not list. Cloud is the fallback: it is the only other
 * group the surface always has.
 *
 * Nothing else is corrected. A user sitting on their own sprite or device is
 * left where they are.
 */
export function correctDiagnosticsTabForServerTarget(input: {
  readonly activeTab: DiagnosticsTab;
  readonly serverTargetVisible: boolean;
}): DiagnosticsTab | null {
  if (input.serverTargetVisible) return null;
  return input.activeTab === "server" ? "cloud" : null;
}

export function providerAuthorityForSelection(input: {
  readonly selectedEnvironmentId: EnvironmentId | null;
  readonly runtimeStates: Readonly<Record<EnvironmentId, SavedEnvironmentRuntimeState>>;
}): RuntimeAuthorityRenewalState | null {
  if (input.selectedEnvironmentId === null) return null;
  return input.runtimeStates[input.selectedEnvironmentId]?.providerAuthority ?? null;
}

/**
 * Returns whether any diagnostics source is still in flight, keeping the
 * Recheck control busy until every source has settled.
 */
export function diagnosticsRefreshing(input: {
  readonly canonicalFetching: boolean;
  readonly cloudChecking: boolean;
  readonly localServerPending: boolean;
  readonly cloudTracesPending: boolean;
  readonly cloudInventoryPending: boolean;
}): boolean {
  return (
    input.canonicalFetching ||
    input.cloudChecking ||
    input.localServerPending ||
    input.cloudTracesPending ||
    input.cloudInventoryPending
  );
}
